/* eslint-disable max-len */
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DateRangePickerField from 'common/forms/DateRangePickerField';
import { selectAuthorizedFacilities } from 'store/selectors/facilitySelectors';
import { filterReversalQueue } from 'store/thunks/reversalQueueThunks';
import { getStateDisplayName } from 'store/utils/shipmentUtils';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';

const ReversalReviewFilterButton = () => {
  const dispatch = useDispatch();

  const pharmacies = useSelector(selectAuthorizedFacilities);
  const filter = useSelector(({ inventory }) => inventory.reversalQueue.filter);

  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);

  // Local draft state for popover fields — synced from Redux when filter changes
  const [lotNumber, setLotNumber] = useState('');
  const [drugName, setDrugName] = useState('');
  const [ndc, setNdc] = useState('');

  // Sync draft state when Redux filter is updated externally (e.g. chip remove)
  useEffect(() => {
    setLotNumber(filter?.lotNumber ?? '');
    setDrugName(filter?.drugName ?? '');
    setNdc(filter?.ndc ?? '');
  }, [filter?.lotNumber, filter?.drugName, filter?.ndc]);

  const activeFilterCount = [
    filter?.lotNumber,
    filter?.drugName,
    filter?.ndc,
    filter?.createdAtFrom || filter?.createdAtTo,
    filter?.isUnverified,
  ].filter(Boolean).length;

  const selectedFacility = filter?.fulfillingPharmacyId ?? 'ALL';

  const handleFacilityChange = (e) => {
    dispatch(
      filterReversalQueue({
        filter: {
          ...filter,
          fulfillingPharmacyId: e.target.value !== 'ALL' ? e.target.value : undefined,
        },
      })
    );
  };

  // POM-6087 - applied on change rather than on Apply, matching Facility and Date Range.
  // The two text fields below are drafts because they are typed; a switch has nothing to
  // finish ticking, and leaving it unapplied reads as a filter that does not work.
  //
  // Unticked sends undefined, not false: the API reads false as "the verified rows only",
  // which is a third state this screen deliberately does not offer.
  const handleUnverifiedChange = (e) => {
    dispatch(
      filterReversalQueue({
        filter: { ...filter, isUnverified: e.target.checked ? true : undefined },
      })
    );
  };

  const handleDateChange = (range) => {
    dispatch(
      filterReversalQueue({
        filter: {
          ...filter,
          createdAtFrom: range?.[0] || undefined,
          createdAtTo: range?.[1] || undefined,
        },
      })
    );
  };

  const handleApplyFilters = () => {
    dispatch(
      filterReversalQueue({
        filter: {
          ...filter,
          lotNumber: lotNumber || undefined,
          drugName: drugName || undefined,
          ndc: ndc || undefined,
        },
      })
    );
    setFiltersAnchorEl(null);
  };

  const handleResetFilters = () => {
    dispatch(
      filterReversalQueue({
        filter: {
          ...filter,
          lotNumber: undefined,
          drugName: undefined,
          ndc: undefined,
          createdAtFrom: undefined,
          createdAtTo: undefined,
          isUnverified: undefined,
        },
      })
    );
    setFiltersAnchorEl(null);
  };

  return (
    <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end'>
      <FormControl size='small' sx={{ minWidth: 220 }}>
        <InputLabel id='reversal-facility-label'>Facility</InputLabel>
        <Select
          labelId='reversal-facility-label'
          label='Facility'
          value={selectedFacility}
          onChange={handleFacilityChange}
          displayEmpty
        >
          <MenuItem value='ALL'>All Facilities</MenuItem>
          {pharmacies.map((p) => (
            <MenuItem key={p.fulfillingPharmacyId} value={p.fulfillingPharmacyId}>
              {p.state ? `${p.name} (${getStateDisplayName(p.state)})` : p.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Badge badgeContent={activeFilterCount} color='primary' invisible={activeFilterCount === 0}>
        <Button
          sx={{ height: 33 }}
          variant='outlined'
          color='secondary'
          onClick={(e) => setFiltersAnchorEl(e.currentTarget)}
        >
          Filters
        </Button>
      </Badge>

      <Popover
        open={Boolean(filtersAnchorEl)}
        anchorEl={filtersAnchorEl}
        onClose={() => setFiltersAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
            Filters
          </Typography>
          {/* First, because it is the one that changes what KIND of work the queue is
              showing. A {SYSTEM_DEFICIT.toLowerCase()} claim has no receiving record behind it,
              so approving
              it back into stock credits quantity the pharmacy was never shown to have -
              a different decision from restocking an ordinary reservation, and one a
              reviewer may want to work through on its own. */}
          <FormControlLabel
            control={
              <Checkbox
                size='small'
                checked={Boolean(filter?.isUnverified)}
                onChange={handleUnverifiedChange}
                inputProps={{ 'data-testid': 'unverified-only-checkbox' }}
              />
            }
            label={<Typography variant='body2'>{SYSTEM_DEFICIT} claims only</Typography>}
            sx={{ m: 0 }}
          />
          <DateRangePickerField
            label='Date Range'
            onDateChange={handleDateChange}
            initialValue={
              filter?.createdAtFrom && filter?.createdAtTo
                ? [filter.createdAtFrom, filter.createdAtTo]
                : undefined
            }
          />
          <TextField
            label='Lot Number'
            size='small'
            value={lotNumber}
            onChange={(e) => setLotNumber(e.target.value)}
            fullWidth
          />
          <TextField
            label='Drug Name'
            size='small'
            value={drugName}
            onChange={(e) => setDrugName(e.target.value)}
            fullWidth
          />
          <TextField
            label='NDC'
            size='small'
            value={ndc}
            onChange={(e) => setNdc(e.target.value)}
            fullWidth
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button size='small' onClick={handleResetFilters}>
              Reset
            </Button>
            <Button size='small' variant='contained' onClick={handleApplyFilters}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Stack>
  );
};

export default ReversalReviewFilterButton;
